/**
 * Generates monthly date ranges for a specified year
 * @param year - The year to generate dates for (e.g., 2022)
 * @returns Array of objects with value and label properties for each month
 */
export function generateMonthlyDateRanges(year: number) {
  const months = [
    { name: "JAN", days: 31 },
    { name: "FEB", days: isLeapYear(year) ? 29 : 28 },
    { name: "MAR", days: 31 },
    { name: "APR", days: 30 },
    { name: "MAY", days: 31 },
    { name: "JUN", days: 30 },
    { name: "JUL", days: 31 },
    { name: "AUG", days: 31 },
    { name: "SEP", days: 30 },
    { name: "OCT", days: 31 },
    { name: "NOV", days: 30 },
    { name: "DEC", days: 31 },
  ];

  // Format year for label (e.g., 2022 becomes "22")
  const yearLabel = year.toString().slice(-2);

  return months.map((month, index) => {
    // Month index is 0-based, so add 1 for the date format
    const monthNum = (index + 1).toString().padStart(2, "0");

    // Start date is the first day of the month
    const startDate = `${year}-${monthNum}-01`;

    // End date is the last day of the month
    const endDate = `${year}-${monthNum}-${month.days}`;

    return {
      value: `${startDate},${endDate}`,
      label: `${month.name}-${yearLabel}`,
    };
  });
}

/**
 * Generates monthly date ranges for a custom date range
 * @param startYear - The starting year (e.g., 2023)
 * @param endYear - The ending year (e.g., 2025) or "current" to limit to current month
 * @param startMonth - The starting month (1-12), defaults to 1 (January)
 * @param endMonth - The ending month (1-12), defaults to 12 (December)
 * @returns Array of objects with value and label properties for each month in the range
 */
export function generateCustomDateRange(
  startYear: number,
  endYear: number | "current",
  startMonth: number = 1,
  endMonth: number = 12,
) {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 1-based month

  // Determine actual end year and month
  const actualEndYear =
    endYear === "current" ? currentYear : (endYear as number);
  const actualEndMonth = endYear === "current" ? currentMonth : endMonth;

  // Ensure valid parameters
  if (startYear > actualEndYear) {
    throw new Error("Start year must be less than or equal to end year");
  }
  if (
    startMonth < 1 ||
    startMonth > 12 ||
    actualEndMonth < 1 ||
    actualEndMonth > 12
  ) {
    throw new Error("Months must be between 1 and 12");
  }
  if (startYear === actualEndYear && startMonth > actualEndMonth) {
    throw new Error(
      "Start month must be less than or equal to end month in the same year",
    );
  }

  const months = [
    { name: "JAN", days: 31 },
    { name: "FEB", days: 28 }, // Will be adjusted for leap years
    { name: "MAR", days: 31 },
    { name: "APR", days: 30 },
    { name: "MAY", days: 31 },
    { name: "JUN", days: 30 },
    { name: "JUL", days: 31 },
    { name: "AUG", days: 31 },
    { name: "SEP", days: 30 },
    { name: "OCT", days: 31 },
    { name: "NOV", days: 30 },
    { name: "DEC", days: 31 },
  ];

  const result = [];

  for (let year = startYear; year <= actualEndYear; year++) {
    // Determine the month range for this year
    let yearStartMonth = year === startYear ? startMonth : 1;
    let yearEndMonth = year === actualEndYear ? actualEndMonth : 12;

    // Adjust February for leap years
    const adjustedMonths = months.map((month, index) => ({
      ...month,
      days: month.name === "FEB" ? (isLeapYear(year) ? 29 : 28) : month.days,
    }));

    for (
      let monthIndex = yearStartMonth - 1;
      monthIndex < yearEndMonth;
      monthIndex++
    ) {
      const month = adjustedMonths[monthIndex];
      const monthNum = (monthIndex + 1).toString().padStart(2, "0");
      const yearLabel = year.toString().slice(-2);

      const startDate = `${year}-${monthNum}-01`;
      const endDate = `${year}-${monthNum}-${month.days}`;

      result.push({
        value: `${startDate},${endDate}`,
        label: `${month.name}-${yearLabel}`,
      });
    }
  }

  return result;
}

/**
 * Generates monthly date ranges for a range of years
 * @param startYear - The starting year (e.g., 2020)
 * @param endYear - The ending year (e.g., 2022)
 * @param startMonth - Optional start month (1-12 or month name like JAN)
 * @param endMonth - Optional end month (1-12 or month name like DEC)
 * @returns Array of objects with value and label properties for each month across all years
 */
export function generateDateRangesForYearRange(
  startYear: number,
  endYear: number,
  startMonth?: number | string,
  endMonth?: number | string,
) {
  const monthNameToNumber: Record<string, number> = {
    JAN: 1,
    FEB: 2,
    MAR: 3,
    APR: 4,
    MAY: 5,
    JUN: 6,
    JUL: 7,
    AUG: 8,
    SEP: 9,
    OCT: 10,
    NOV: 11,
    DEC: 12,
  };

  const resolveMonth = (
    month: number | string | undefined,
    defaultMonth: number,
  ) => {
    if (month === undefined || month === null) {
      return defaultMonth;
    }

    if (typeof month === "number") {
      if (month < 1 || month > 12) {
        throw new Error("Months must be between 1 and 12");
      }
      return month;
    }

    const normalizedMonth = month.trim().slice(0, 3).toUpperCase();
    const monthNumber = monthNameToNumber[normalizedMonth];

    if (!monthNumber) {
      throw new Error("Invalid month name. Use values like JAN, FEB, ... DEC");
    }

    return monthNumber;
  };

  const resolvedStartMonth = resolveMonth(startMonth, 1);
  const resolvedEndMonth = resolveMonth(endMonth, 12);

  // Ensure valid year range
  if (startYear > endYear) {
    throw new Error("Start year must be less than or equal to end year");
  }
  if (startYear === endYear && resolvedStartMonth > resolvedEndMonth) {
    throw new Error(
      "Start month must be less than or equal to end month in the same year",
    );
  }

  const result = [];

  // Generate dates for each year in the range
  for (let year = startYear; year <= endYear; year++) {
    const monthlyRanges = generateMonthlyDateRanges(year);

    const yearStartMonth = year === startYear ? resolvedStartMonth : 1;
    const yearEndMonth = year === endYear ? resolvedEndMonth : 12;

    result.push(...monthlyRanges.slice(yearStartMonth - 1, yearEndMonth));
  }

  // Latest month first
  return result.reverse();
}

// Example usage:
// const dateRanges = generateDateRangesForYearRange(2020, 2022);
// console.log(dateRanges);

/**
 * Helper function to determine if a year is a leap year
 */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

// Example usage:
// const dateRanges2022 = generateMonthlyDateRanges(2022);
// console.log(dateRanges2022);

export const GROUP_BY = ["Total", "Discom"];

export const DEFAULT_DATES = [
  { value: "2022-06-01 to 2022-06-30", label: "JUN-22" },
].reverse();

/**
 * Generates monthly date ranges for the last N months from current date
 * @param numberOfMonths - Number of months to go back (default: 12)
 * @returns Array of objects with value and label properties for recent months
 */
export function generateLastNMonths(numberOfMonths: number = 12) {
  const result = [];
  const currentDate = new Date();

  for (let i = 0; i < numberOfMonths; i++) {
    // Create a new date for each iteration
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - i,
      1,
    );
    const year = date.getFullYear();
    const month = date.getMonth();

    // Get month info
    const months = [
      { name: "JAN", days: 31 },
      { name: "FEB", days: isLeapYear(year) ? 29 : 28 },
      { name: "MAR", days: 31 },
      { name: "APR", days: 30 },
      { name: "MAY", days: 31 },
      { name: "JUN", days: 30 },
      { name: "JUL", days: 31 },
      { name: "AUG", days: 31 },
      { name: "SEP", days: 30 },
      { name: "OCT", days: 31 },
      { name: "NOV", days: 30 },
      { name: "DEC", days: 31 },
    ];

    const monthInfo = months[month];
    const monthNum = (month + 1).toString().padStart(2, "0");
    const yearLabel = year.toString().slice(-2);

    // Start date is the first day of the month
    const startDate = `${year}-${monthNum}-01`;

    // End date is the last day of the month
    const endDate = `${year}-${monthNum}-${monthInfo.days}`;

    result.push({
      value: `${startDate},${endDate}`,
      label: `${monthInfo.name}-${yearLabel}`,
    });
  }

  return result; // Already in reverse chronological order
}

/**
 * Generates monthly date ranges from 2021 up to the current month
 * @returns Array of objects with value and label properties for each month from 2021 to current
 */
export function generateMonthsFrom2021ToLatest() {
  const startYear = 2021;
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0-based (0 = January, 11 = December)

  const result = [];

  // Generate dates for each year from 2021 to current year
  for (let year = startYear; year <= currentYear; year++) {
    const monthlyRanges = generateMonthlyDateRanges(year);

    if (year === currentYear) {
      // For current year, only include months up to current month
      result.push(...monthlyRanges.slice(0, currentMonth + 1));
    } else {
      // For previous years, include all months
      result.push(...monthlyRanges);
    }
  }

  return result.reverse(); // Reverse to show latest months first
}

/**
 * Generates monthly date ranges from a specified start year up to the current month
 * @param startYear - The year to start from (default: 2021)
 * @returns Array of objects with value and label properties for each month
 */

// Update the DEFAULT_DATES to use the new function
export const DEFAULT_DATES_FROM_2021 = generateMonthsFrom2021ToLatest();

export const DISCOM = [
  {
    label: "TPWODL",
    key: "TPWODL",
  },
  {
    label: "TPSODL",
    key: "TPSODL",
  },
  {
    label: "TPNODL",
    key: "TPNODL",
  },
  {
    label: "TPCODL",
    key: "TPCODL",
  },
];
